import GlobalTable from "@/components/probaworkflows_base_components/GlobalTable/GlobalTable.vue";
import {
  get_headers,
  get_project_id,
  get_token,
  UPDATE_TAG_URL,
  LIST_DATA_POINTS_URL,
  CREATE_DATA_POINT_URL,
  UPDATE_DATA_POINT_URL,
  DELETE_DATA_POINT_URL,
  GET_ASSIGNED_DATA_POINTS,
  ASSIGN_UPDATE_DATA_POINT,
  UNASSIGN_UPDATE_DATA_POINT,
} from "@/utils/constants/constants";
import alert_mixin from "@/utils/mixins/alert_mixin";
import formatting_mixins from "@/utils/mixins/formatting_mixins";
import { validate_non_empty_string } from "@/utils/validations/validate_inputs";
import axios from "axios";
import "datatables.net";
import "datatables.net-dt/css/dataTables.dataTables.css";

export default {
  name: "DataPointView",
  mixins: [alert_mixin, formatting_mixins],
  components: { GlobalTable },
  data: () => ({
    tag_types: [
      { value: 'text', label: 'Text Field' },
      { value: 'signature', label: 'Signature' },
      { value: 'initials', label: 'Initials' },
      { value: 'date', label: 'Date' },
      { value: 'datenow', label: 'Current Date (Auto-filled)' },
      { value: 'image', label: 'Image Upload' },
      { value: 'file', label: 'File Upload' },
      { value: 'payment', label: 'Payment' },
      { value: 'stamp', label: 'Stamp' },
      { value: 'select', label: 'Dropdown Select' },
      { value: 'checkbox', label: 'Checkbox' },
      { value: 'multiple', label: 'Multiple Select' },
      { value: 'radio', label: 'Radio Buttons' },
      { value: 'phone', label: 'Phone Number' },
      { value: 'cells', label: 'Cells' }
    ],
    is_editing_tag: false,
    editing_tag_id: null,
    original_tag_state: null,
    selected_tag_type: 'text',
    tag_options: '',
    data_points: [],
    table_safe_data_points: [],
    selected_data_point: {
      id: null,
      data_point_name: "",
      data_point_description: "",
      date_created: "",
      date_modified: "",
    },
    new_data_point: {
      name: "",
      description: "",
    },
    data_point_to_delete: null,
    active_data_points_count: 0,
    recent_data_points_count: 0,
    updated_data_points_count: 0,
    project_id: get_project_id(),
    token: get_token(),
    headers: get_headers(),
    loading: false,
    delete_loading: false,
    show_create_data_point_modal: false,
    show_assign_modal: false,
    selected_data_point_id: null,
    new_tag_name: "",
    assignable_data_points: [],
    current_assignments: [],
    is_loading: false,
    assignment_loading: false,
    update_loading: false,
    show_tag_input_error: false,
  }),
  mounted() {
    this.$nextTick(() => {
      this.get_data_points();
    });
  },
  methods: {
    async get_data_points() {
      this.loading = true;
      try {
        const response = await axios.get(LIST_DATA_POINTS_URL, {
          headers: this.headers,
          params: { project_id: this.project_id },
        });

        if (response.data.status === "success") {
          this.data_points = response.data.data;
          this.table_safe_data_points = this.data_points.map((dp) => ({
            id: dp.id,
            name: dp.data_point_name,
            description: dp.data_point_description,
            date_created: this.format_date(dp.date_created),
            date_modified: this.format_date(dp.date_modified),
          }));
          this.calculate_counts();
        } else {
          this.show_error(
            response.data.message || "Failed to fetch data points"
          );
        }
      } catch (error) {
        const error_msg = error.response?.data?.message || error.message;
        this.show_error(`Error fetching data points: ${error_msg}`);
      } finally {
        this.loading = false;
      }
    },

    select_data_point(data_point) {
      this.selected_data_point = {
        id: data_point.id,
        data_point_name: data_point.name,
        data_point_description: data_point.description,
        date_created: data_point.date_created,
        date_modified: data_point.date_modified,
      };
    },

    async create_data_point() {
      if (!this.validate_data_point_input()) return;

      this.loading = true;

      try {
        const payload = {
          project_id: this.project_id,
          data_point_name: this.new_data_point.name.trim(),
          data_point_description: this.new_data_point.description.trim(),
        };

        const response = await axios.post(CREATE_DATA_POINT_URL, payload, {
          headers: this.headers,
        });

        if (response.data.status === "success") {
          this.show_success("Data point created successfully");
          this.new_data_point = { name: "", description: "" };
          this.close_modal("create_data_point_modal");

          if (this._isMounted) {
            await this.get_data_points();
          }
        } else {
          this.show_error(response.data.message);
        }
      } catch (error) {
        if (error.response?.data?.message) {
          this.show_error(error.response.data.message);
        }
      } finally {
        this.loading = false;
      }
    },

    get_update_payload() {
      let variable = {
        data_point_id: this.selected_data_point.id,
        data_point_name: this.selected_data_point.data_point_name,
        data_point_description: this.selected_data_point.data_point_description,
      };
      return variable;
    },

    async update_data_point() {
      if (!this.validate_data_point_input(true)) return;

      this.loading = true;

      try {
        const response = await axios.post(
          UPDATE_DATA_POINT_URL,
          this.get_update_payload(),
          { headers: this.headers }
        );

        const response_data = this.parse_response_data(response.data);

        if (response_data.status === "success") {
          this.show_success(
            response_data.message || "Data point updated successfully"
          );
          await this.get_data_points();
          this.close_modal("update_data_point_modal");
        } else {
          this.handle_update_error(response_data);
        }
      } catch (error) {
        this.handle_update_exception(error);
      } finally {
        this.loading = false;
      }
    },

    parse_response_data(data) {
      return typeof data === "string" ? JSON.parse(data) : data;
    },

    handle_update_error(response_data) {
      this.show_error(response_data.message || "Failed to update data point");

      if (response_data.errors) {
        const error_messages = Object.values(response_data.errors).join(", ");
        this.show_error(`Validation errors: ${error_messages}`);
      }
    },

    handle_update_exception(error) {
      let error_msg = "Update failed";

      if (error.response) {
        if (error.response.data.data) {
          error_msg = Object.values(error.response.data.data).join(", ");
        } else {
          const response_data = this.parse_response_data(error.response.data);
          error_msg = response_data.message || error_msg;
        }
      } else {
        error_msg = error.message || error_msg;
      }

      this.show_error(error_msg);
    },

    async delete_data_point(data_point_id) {
      this.show_confirm(
        "Are you sure you want to delete?",
        "You won't be able to revert this!",
        () => this.handle_delete_confirmed(data_point_id)
      );
    },

    async handle_delete_confirmed(data_point_id) {
      this.delete_loading = true;

      try {
        const response = await axios.delete(DELETE_DATA_POINT_URL, {
          data: {
            data_point_id: data_point_id,
            project_id: this.project_id,
          },
          headers: this.headers,
        });

        const response_data = this.parse_response_data(response.data);

        if (response_data.status === "success") {
          this.show_success(
            response_data.message || "Data point deleted successfully"
          );
          await this.get_data_points();
        } else {
          this.handle_delete_error(response_data);
        }
      } catch (error) {
        this.handle_delete_exception(error);
      } finally {
        this.delete_loading = false;
      }
    },

    handle_delete_error(response_data) {
      this.show_error(response_data.message || "An unexpected error occurred.");
      if (response_data.errors) {
        const error_messages = Object.values(response_data.errors).join(", ");
        this.show_error(`Validation errors: ${error_messages}`);
      }
    },

    handle_delete_exception(error) {
      let error_msg = "Failed to delete data point";
      if (error.response) {
        if (error.response.data?.data) {
          error_msg = Object.values(error.response.data.data).join(", ");
        } else {
          const response_data = this.parse_response_data(error.response.data);
          error_msg = response_data.message || error_msg;
        }
      } else {
        error_msg = error.message || error_msg;
      }
      this.show_error(error_msg);
    },

    validate_data_point_input(is_update = false) {
      const name = is_update
        ? this.selected_data_point.data_point_name
        : this.new_data_point.name;

      if (!name) {
        this.show_error("Data point name is required");
        return false;
      }

      if (!validate_non_empty_string(name)) {
        this.show_error("Data point name must not be empty");
        return false;
      }

      return true;
    },

    calculate_counts() {
      this.active_data_points_count = this.data_points.length;

      const one_week_ago = new Date();
      one_week_ago.setDate(one_week_ago.getDate() - 7);

      this.recent_data_points_count = this.data_points.filter((dp) => {
        return new Date(dp.date_created) > one_week_ago;
      }).length;

      this.updated_data_points_count = this.data_points.filter((dp) => {
        return (
          new Date(dp.date_modified) > one_week_ago &&
          new Date(dp.date_modified).getTime() !==
          new Date(dp.date_created).getTime()
        );
      }).length;
    },

    format_date(date_string) {
      if (!date_string || date_string === "null") return "N/A";
      const date = new Date(date_string);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },

    async open_assign_modal() {
      this.show_assign_modal = true;
      this.new_tag_name = "";
      this.selected_data_point_id = "";
      await this.fetch_current_assignments();
    },

    async fetch_current_assignments() {
      const response = await axios.get(GET_ASSIGNED_DATA_POINTS, {
        headers: this.headers,
        params: { project_id: this.project_id },
      });

      if (response.data.status === "success") {
        this.current_assignments = response.data.data.map((assignment) => ({
          ...assignment,
          is_loading: false,
        }));
      } else {
        this.show_error(response.data.message || "Failed to fetch assignments");
      }
    },

    async assign_new_tag() {
      if (!this.new_tag_name || !this.selected_data_point_id) return;

      this.assignment_loading = true;
      try {
        const payload = {
          data_point_id: this.selected_data_point_id,
          tag_name: this.new_tag_name,
          project_id: this.project_id,
          tag_type: this.selected_tag_type,
          tag_options: this.tag_options
        };

        const response = await axios.post(
          ASSIGN_UPDATE_DATA_POINT,
          payload,
          { headers: this.headers }
        );

        if (response.data.status === "success") {
          this.show_success("Tag assigned successfully");
          await this.fetch_current_assignments();
          this.new_tag_name = "";
          this.selected_data_point_id = "";
          this.tag_options = "";
          this.selected_tag_type = "text";
          this.is_editing_tag = false;
        } else {
          this.show_error(response.data.message || "Failed to assign tag");
        }
      } catch (error) {
        const error_msg = error.response?.data?.message || error.message;
        this.show_error(`Error assigning tag: ${error_msg}`);
      } finally {
        this.assignment_loading = false;
      }
    },

    async unassign_tag(assignment) {
      assignment.is_loading = true;
      try {
        const dataPoint = this.data_points.find(
          (dp) => dp.data_point_name === assignment.data_point_name
        );

        if (!dataPoint) {
          this.show_error("Could not find data point with the given name");
          return;
        }

        const payload = {
          data_point_id: dataPoint.id,
          tag_name: assignment.tag_name,
          project_id: this.project_id,
        };

        const config = {
          headers: this.headers,
        };

        const response = await axios.post(
          UNASSIGN_UPDATE_DATA_POINT,
          payload,
          config
        );

        if (response.data.status === "success") {
          this.show_success(
            response.data.message || "Tag unassigned successfully"
          );
          await this.fetch_current_assignments();
        } else {
          this.show_error(response.data.message || "Failed to unassign tag");
        }
      } catch (error) {
        const error_msg =
          error.response?.data?.message ||
          error.message ||
          "Unexpected error occurred";
        this.show_error(`Error unassigning tag: ${error_msg}`);
      } finally {
        assignment.is_loading = false;
      }
    },

    edit_tag(assignment) {
      this.is_editing_tag = true;
      this.editing_tag_id = assignment.id;
      this.original_tag_state = {
        name: this.new_tag_name,
        data_point_id: this.selected_data_point_id,
        type: this.selected_tag_type,
        options: this.tag_options
      };

      this.new_tag_name = assignment.tag_name;
      this.selected_data_point_id = assignment.data_point_id;
      this.selected_tag_type = assignment.tag_type;
      this.tag_options = assignment.tag_options || '';
    },

    async update_tag() {
      if (!this.new_tag_name || !this.selected_data_point_id) return;

      this.update_loading = true;
      try {
        const payload = {
          tag_id: this.editing_tag_id,
          tag_name: this.new_tag_name,
          tag_type: this.selected_tag_type,
          tag_options: this.tag_options
        };

        const response = await axios.post(
          UPDATE_TAG_URL,
          payload,
          { headers: this.headers }
        );

        if (response.data.status === "success") {
          this.show_success("Tag updated successfully");
          await this.fetch_current_assignments();
          this.reset_tag_form();
        } else {
          this.show_error(response.data.message || "Failed to update tag");
        }
      } catch (error) {
        const error_msg = error.response?.data?.message || error.message;
        this.show_error(`Error updating tag: ${error_msg}`);
      } finally {
        this.update_loading = false;
      }
    },

    cancel_edit() {
      if (this.original_tag_state) {
        this.new_tag_name = this.original_tag_state.name;
        this.selected_data_point_id = this.original_tag_state.data_point_id;
        this.selected_tag_type = this.original_tag_state.type;
        this.tag_options = this.original_tag_state.options;
      }
      this.is_editing_tag = false;
      this.editing_tag_id = null;
      this.original_tag_state = null;
    },

    reset_tag_form() {
      this.new_tag_name = "";
      this.selected_data_point_id = "";
      this.selected_tag_type = "text";
      this.tag_options = "";
      this.is_editing_tag = false;
      this.editing_tag_id = null;
      this.original_tag_state = null;
    }
  }
};